import React, { useState, useEffect, useRef, useCallback } from "react";
import { GoogleMap, MarkerF, InfoWindowF, PolylineF } from "@react-google-maps/api";
import notifications from "../../mockdata/notifications";
import greenBus from "../../assets/green_bus.svg";
import redBus from "../../assets/Red_Bus.svg";
import orangeBus from "../../assets/Orange_Bus.svg";
import {
  sourceLocation,
  destinationGreenIcon,
  rightArrowExtended,
  videoIcon,
  speedIcon,
  fuelIcon,
  distanceCoveredIcon,
  stopsIcon,
  driverIcon,
  busesIcon,
} from "assets/icons";
import {
  RootContainer,
  TitleContainer,
  DefaultBody,
  BodyText,
  ExpandedBody,
  DetailItems,
  FooterText,
  DetailItems2,
  DetailItemsValue,
  DetailItems1,
  DetailsContainer1,
  DetailsContainer,
  IconText,
  IconTextValue,
  DetailItemTitle,
  DetailItemContent,
  VideoIcon,
} from "./styles";
import Button from "../Button/button";
import { FlexSpace, HorizontalSpace, VerticalSpace } from "../Space/space";

const containerStyle = {
  width: "100%",
  height: "calc(100vh - 108px)",
};

const center = {
  lat: 24.4539,
  lng: 54.3773,
};

const GMap = (props) => {
  const { coOrdinates, activeBusType, tabIndex, handleViewDetails, hideCallout, zoom, customapContainerStyle } = props;
  const [activeMarker, setActiveMarker] = useState(null);
  const [, setPath] = useState([]);
  const [animationPath, setAnimationPath] = useState([]);
  const [movingLocation, setMovingLocation] = useState(null);
  const [progress, setProgress] = useState(0);
  const mapRef = useRef(null);

  const events = notifications?.notifications?.events?.eventsList || [];
  const incidents = notifications?.notifications?.incidents?.incidentsList || [];
  const oprAlerts = notifications?.notifications?.oprAlerts?.oprAlertsList || [];

  const onLoad = useCallback((map) => {
       mapRef.current = map;
   }, []);
 
   const resetMapState = useCallback(() => {
      setActiveMarker(null);
      setPath([]);
      setAnimationPath([]);
      setMovingLocation(null);
      setProgress(0);
      if (mapRef.current) {
          mapRef.current.panTo(center);
          mapRef.current.setZoom(zoom || 10);
      }
   }, [zoom]);

   useEffect(() => {
     resetMapState();
   }, [tabIndex, resetMapState]);

   useEffect(() => {
     // Check for both nested (raw) and flattened (formatted) coordinate structures
     const sourcePos = coOrdinates?.sourceCoOrdinates || coOrdinates?.source?.coOrdinates;
     const destPos = coOrdinates?.destinationCoOrdinates || coOrdinates?.destination?.coOrdinates;
     const currentPos = coOrdinates?.currentLocationCoOrdinates || coOrdinates?.currentLocation?.coOrdinates;

     if (sourcePos && destPos) {
       console.log("Fetching directions for:", coOrdinates);
       
       // Initialize moving location immediately to avoid flicker/disappearance
       if (currentPos) {
           setMovingLocation(currentPos);
       }

       const directionsService = new window.google.maps.DirectionsService();

       directionsService.route(
         {
           origin: sourcePos,
           destination: destPos,
           travelMode: window.google.maps.TravelMode.DRIVING,
         },
         (result, status) => {
           if (status === window.google.maps.DirectionsStatus.OK) {
             console.log("Directions found:", result);
             const routePath = result.routes[0].overview_path;
             setPath(routePath);

             // Interpolate path for smooth animation at ~40mph
             // 40 mph ≈ 17.88 m/s. At 100ms updates, step size ≈ 1.79 meters.
             const speedMps = 17.88; 
             const updateIntervalMs = 100;
             const stepDistance = speedMps * (updateIntervalMs / 1000);
             
             const densePath = [];
             for (let i = 0; i < routePath.length - 1; i++) {
                 const p1 = routePath[i];
                 const p2 = routePath[i+1];
                 
                 // Calculate distance (simple spherical approximation)
                 const R = 6371e3; // meters
                 const lat1 = p1.lat() * Math.PI/180;
                 const lat2 = p2.lat() * Math.PI/180;
                 const dLat = (p2.lat()-p1.lat()) * Math.PI/180;
                 const dLon = (p2.lng()-p1.lng()) * Math.PI/180;
                 const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                         Math.cos(lat1) * Math.cos(lat2) *
                         Math.sin(dLon/2) * Math.sin(dLon/2);
                 const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                 const dist = R * c;
 
                 const numSteps = Math.max(1, Math.floor(dist / stepDistance));
                 
                 for (let j = 0; j < numSteps; j++) {
                     const fraction = j / numSteps;
                     const lat = p1.lat() + (p2.lat() - p1.lat()) * fraction;
                     const lng = p1.lng() + (p2.lng() - p1.lng()) * fraction;
                     densePath.push(new window.google.maps.LatLng(lat, lng));
                 }
             }
             // Add the final point
             densePath.push(routePath[routePath.length - 1]);
             
             setAnimationPath(densePath);
             setMovingLocation(densePath[0]);
             setProgress(0);
             setActiveMarker(coOrdinates); 
 
             if (mapRef.current) {
                const bounds = new window.google.maps.LatLngBounds();
                routePath.forEach(point => bounds.extend(point));
                mapRef.current.fitBounds(bounds);
                
                // Prevent excessive zoom
                const listener = window.google.maps.event.addListener(mapRef.current, "idle", () => { 
                    if (mapRef.current.getZoom() > 14) {
                        mapRef.current.setZoom(14); 
                    }
                    window.google.maps.event.removeListener(listener); 
                });
            }
 
           } else {
            console.error(`error fetching directions ${status}`, result);
            // Ensure movingLocation is set even if directions fail so icon is visible
             if (currentPos) {
                setMovingLocation(currentPos);
            }
            setActiveMarker(coOrdinates);
          }
        }
      );
    } else {
         setPath([]);
         setAnimationPath([]);
         setMovingLocation(null);
         setActiveMarker(null);
         if (mapRef.current) {
             mapRef.current.panTo(center);
             mapRef.current.setZoom(zoom || 10);
         }
     }
   }, [coOrdinates, zoom]);

  useEffect(() => {
    let timeoutId;
    if (animationPath.length > 0 && progress < animationPath.length - 1) {
       timeoutId = setTimeout(() => {
         setProgress((prev) => {
            const next = prev + 1;
            if (next >= animationPath.length) return prev; // Stop at destination
            return next;
         });
       }, 100);
    }
    return () => clearTimeout(timeoutId);
  }, [animationPath, progress]);

  useEffect(() => {
      if (animationPath.length > 0 && animationPath[progress]) {
          setMovingLocation(animationPath[progress]);
      }
  }, [progress, animationPath]);

  const getMarkerData = (marker) => {
      // Helper to normalize data structure for InfoWindow
      if (!marker) return null;
      
      const source = marker.sourceLocality || marker.source?.area || marker.source?.locality || (typeof marker.source === 'string' ? marker.source : "N/A");
      const destination = marker.destinationLocality || marker.destination?.area || marker.destination?.locality || (typeof marker.destination === 'string' ? marker.destination : "N/A");
      const current = marker.currentLocality || marker.currentLocation?.area || marker.currentLocation?.locality || (typeof marker.currentLocation === 'string' ? marker.currentLocation : "N/A");
      const next = marker.nextStop?.area || marker.nextStop?.locality || (typeof marker.nextStop === 'string' ? marker.nextStop : "N/A");

      return {
          incident: marker.incident || marker.title?.split('|')[0]?.trim() || marker.title || "N/A",
          busNo: marker.busNo || marker.title?.split('|')[1]?.replace('BUS#', '')?.trim() || marker.busId || "N/A",
          route: marker.route || marker.title?.split('|')[2]?.replace('Route -', '')?.trim() || "N/A",
          sourceLocality: source,
          destinationLocality: destination,
          driver: marker.driver?.replace('Driver - ', '') || marker.driver || "N/A",
          licenceNo: marker.licenceNo || marker.licenseNumber?.replace('License Number - ', '') || marker.licenseNumber || "N/A",
          currentLocality: current,
          nextStop: next,
          timeStamp: marker.timeStamp || "Just now",
          speed: marker.speed || "N/A",
          fuelConsumed: marker.fuelConsumed || "N/A",
          distance: marker.distanceCovered || marker.distance || "N/A",
          stops: marker.stops || "N/A",
          buses: marker.noOfBuses || marker.buses || "N/A",
          busPlateNo: marker.busLicensePlateNo || marker.licensePlateNo || "N/A",
          imageUrl: marker.avatarImg || marker.imageUrl || "",
      };
  };

  const handleMarkerClick = (marker) => {
    setActiveMarker(marker);
    if (mapRef.current) {
        const position = marker.currentLocation?.coOrdinates || marker.currentLocationCoOrdinates;
        if (position) {
            mapRef.current.panTo(position);
            mapRef.current.setZoom(14);
        }
    }
  };

  const handleCloseInfoWindow = () => {
    setActiveMarker(null);
  };
  
  const getIcon = (type) => {
      switch(type) {
          case 'red': return redBus;
          case 'orange': return orangeBus;
          default: return greenBus;
      }
  }

  const currentIcon = coOrdinates ? getIcon(activeBusType) : greenBus;

  return (
    <GoogleMap
      mapContainerStyle={customapContainerStyle || containerStyle}
      center={center}
      zoom={zoom || 10}
      onLoad={onLoad}
      options={{
        disableDefaultUI: true,
        zoomControl: true,
      }}
    >
      {/* Show static markers. Filter out others if one is active. */}
      
        {/* Events - Green Bus */}
        {events.map((item, index) => {
            if (coOrdinates) return null;
            if (activeMarker && activeMarker !== item) return null;
            return (
            <MarkerF
            key={`event-${index}`}
            position={item.currentLocation.coOrdinates}
            icon={{
                url: greenBus,
                scaledSize: new window.google.maps.Size(40, 40),
            }}
            onClick={() => handleMarkerClick(item)}
            />
        )})}

        {/* Incidents - Red Bus */}
        {incidents.map((item, index) => {
            if (coOrdinates) return null;
            if (activeMarker && activeMarker !== item) return null;
            return (
            <MarkerF
            key={`incident-${index}`}
            position={item.currentLocation.coOrdinates}
            icon={{
                url: redBus,
                scaledSize: new window.google.maps.Size(40, 40),
            }}
            onClick={() => handleMarkerClick(item)}
            />
        )})}

        {/* OprAlerts - Orange Bus */}
        {oprAlerts.map((item, index) => {
            if (coOrdinates) return null;
            if (activeMarker && activeMarker !== item) return null;
            return (
            <MarkerF
            key={`oprAlert-${index}`}
            position={item.currentLocation.coOrdinates}
            icon={{
                url: orangeBus,
                scaledSize: new window.google.maps.Size(40, 40),
            }}
            onClick={() => handleMarkerClick(item)}
            />
        )})}

      {/* Moving Marker */}
      {coOrdinates && movingLocation && (
        <>
            <MarkerF
                position={movingLocation}
                icon={{
                    url: currentIcon,
                    scaledSize: new window.google.maps.Size(50, 50), // Slightly larger
                }}
                zIndex={1000}
                onClick={() => handleMarkerClick(coOrdinates)}
            />
             {/* Completed Path (Solid Blue) */}
             <PolylineF
                path={animationPath.slice(0, progress + 1)}
                options={{
                    strokeColor: "#0000FF",
                    strokeOpacity: 1,
                    strokeWeight: 4,
                }}
            />
            {/* Remaining Path (Dashed Blue) */}
             <PolylineF
                path={animationPath.slice(progress)}
                options={{
                    strokeColor: "#0000FF",
                    strokeOpacity: 0,
                    strokeWeight: 4,
                    icons: [{
                        icon: {
                            path: "M 0,-1 0,1",
                            strokeOpacity: 1,
                            scale: 4,
                            strokeColor: "#0000FF",
                            strokeWeight: 2
                        },
                        offset: "0",
                        repeat: "20px"
                    }]
                }}
            />
        </>
      )}

      {/* InfoWindow */}
      {!hideCallout && activeMarker && (() => {
          const markerData = getMarkerData(activeMarker);
          
          // Determine if this is the moving vehicle
          // We check if we have active coordinates (movement mode) and if the active marker matches the moving bus
          const isMovingVehicle = coOrdinates && (
              (activeMarker.busNo && activeMarker.busNo === coOrdinates.busNo) || 
              (activeMarker === coOrdinates)
          );

          // If it's the moving vehicle, use the dynamic movingLocation. 
          // Otherwise, use the static location from the marker data.
          const position = (isMovingVehicle && movingLocation) 
              ? movingLocation 
              : (activeMarker.currentLocation?.coOrdinates || activeMarker.currentLocationCoOrdinates || { lat: 24.4539, lng: 54.3773 });

          return (
            <InfoWindowF
                position={position}
                onCloseClick={handleCloseInfoWindow}
                options={{
                    pixelOffset: new window.google.maps.Size(0, -40)
                }}
            >
                <RootContainer>
                     <TitleContainer>
                       {tabIndex === 2 && markerData.imageUrl && (
                          <img src={markerData.imageUrl} alt="Driver" style={{ width: 40, height: 40, borderRadius: '50%' }} />
                       )}
                       {tabIndex === 2 && <HorizontalSpace count={3} />}
                       <span>
                         {tabIndex === 0 
                             ? `${markerData.incident} | BUS#${markerData.busNo} | Route - ${markerData.route}`
                             : markerData.incident} 
                       </span>
                       {tabIndex === 1 && (
                          <VideoIcon>
                              <img src={videoIcon} alt="Video" />
                          </VideoIcon>
                       )}
                     </TitleContainer>
                     <VerticalSpace count={1} />
                     <ExpandedBody>
                       <VerticalSpace count={1} />
                       <DefaultBody>
                         <img src={sourceLocation} alt="Source" />
                         <HorizontalSpace count={0.5} />
                         <BodyText expanded={true} source={true}>
                           {markerData.sourceLocality}
                         </BodyText>
                         <HorizontalSpace count={1} />
                         <img src={rightArrowExtended} alt="Arrow" />
                         <HorizontalSpace count={2} />
                         <img src={destinationGreenIcon} alt="Destination" />
                         <HorizontalSpace count={0.5} />
                         <BodyText expanded={true} source={false}>
                           {markerData.destinationLocality}
                         </BodyText>
                       </DefaultBody>
                       <VerticalSpace count={3} />
                       
                       {tabIndex === 0 ? (
                            <DetailItems>
                                <BodyText expanded={null} source={null}>
                                Driver - {markerData.driver} | License Number - {markerData.licenceNo}
                                </BodyText>
                                <VerticalSpace count={4} />
                                <BodyText expanded={null} source={null}>
                                Current Location - {markerData.currentLocality}
                                </BodyText>
                            </DetailItems>
                       ) : (tabIndex === 1 || tabIndex === 3) ? (
                            <DetailsContainer1>
                              <DetailsContainer>
                                {tabIndex === 1 ? (
                                  <DetailItems1>
                                    <img src={speedIcon} alt="Speed" />
                                    <HorizontalSpace count={2} />
                                    <DetailItems>
                                      <IconTextValue>{markerData.speed}</IconTextValue>
                                      <IconText>Speed</IconText>
                                    </DetailItems>
                                  </DetailItems1>
                                ) : (
                                  <DetailItems1>
                                    <img src={busesIcon} alt="Buses" />
                                    <HorizontalSpace count={2} />
                                    <DetailItems>
                                      <IconTextValue>{markerData.buses}</IconTextValue>
                                      <IconText>Buses</IconText>
                                    </DetailItems>
                                  </DetailItems1>
                                )}
                                <DetailItems1>
                                  <img src={fuelIcon} alt="Fuel" />
                                  <HorizontalSpace count={2} />
                                  <DetailItems>
                                    <IconTextValue>{markerData.fuelConsumed}</IconTextValue>
                                    <IconText>fuel Consumed</IconText>
                                  </DetailItems>
                                </DetailItems1>
                                <DetailItems1>
                                  <img src={distanceCoveredIcon} alt="Distance" />
                                  <HorizontalSpace count={2} />
                                  <DetailItems>
                                    <IconTextValue>{markerData.distance}</IconTextValue>
                                    <IconText>Distance Covered</IconText>
                                  </DetailItems>
                                </DetailItems1>
                                <DetailItems1>
                                  <img src={stopsIcon} alt="Stops" />
                                  <HorizontalSpace count={2} />
                                  <DetailItems>
                                    <IconTextValue>{markerData.stops}</IconTextValue>
                                    <IconText>Stops</IconText>
                                  </DetailItems>
                                </DetailItems1>
                                <DetailItems1>
                                  <img src={driverIcon} alt="Driver" />
                                  <HorizontalSpace count={2} />
                                  <DetailItems>
                                    <IconTextValue>{markerData.driver}</IconTextValue>
                                    <IconText>Driver</IconText>
                                  </DetailItems>
                                </DetailItems1>
                              </DetailsContainer>
                              <VerticalSpace count={6} />
                              <DetailItems1>
                                <DetailItems>
                                  <DetailItemsValue currentLocation={true}>
                                    {markerData.currentLocality}
                                  </DetailItemsValue>
                                  <DetailItems2>Current Location</DetailItems2>
                                </DetailItems>
                                <HorizontalSpace count={10} />
                                <DetailItems>
                                  <DetailItemsValue currentLocation={false}>
                                    {markerData.nextStop}
                                  </DetailItemsValue>
                                  <DetailItems2>Next Stop</DetailItems2>
                                </DetailItems>
                              </DetailItems1>
                            </DetailsContainer1>
                       ) : (
                            <DetailsContainer>
                              <DetailItems>
                                <DetailItemTitle>{markerData.licenceNo}</DetailItemTitle>
                                <DetailItemContent>License Number</DetailItemContent>
                              </DetailItems>
                              <DetailItems>
                                <DetailItemTitle>{markerData.busNo}</DetailItemTitle>
                                <DetailItemContent>Bus Number</DetailItemContent>
                              </DetailItems>
                              <DetailItems>
                                <DetailItemTitle>{markerData.busPlateNo}</DetailItemTitle>
                                <DetailItemContent>Bus Licence Plate Number</DetailItemContent>
                              </DetailItems>
                            </DetailsContainer>
                       )}

                       <VerticalSpace count={4} />
                       <FooterText>
                         <Button
                           disable={tabIndex === 0}
                           variant="contained"
                           handleClick={() => handleViewDetails(tabIndex, activeMarker)}
                         >
                           {tabIndex === 0 ? "Take Action" : "View Details"}
                         </Button>
                         <FlexSpace />
                         <div style={{ fontSize: "12px", color: "#666" }}>
                           {markerData.timeStamp}
                         </div>
                       </FooterText>
                     </ExpandedBody>
                   </RootContainer>
            </InfoWindowF>
          );
      })()}
    </GoogleMap>
  );
};

export default GMap;
